/**
 * AppNavigator.web.tsx — Web-native navigation implementation.
 *
 * Uses standard web URL routing (window.location / browser popstate)
 * and React state to switch screens cleanly without native mobile stack dependencies
 * (no @react-navigation/native-stack, no react-native-screens, no codegenNativeComponent).
 *
 * Vite resolves .web.tsx before .tsx, so this is used automatically for web builds.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { SHORT_URL_CODE_REGEX, SHORT_URL_ORIGIN } from '@share/networking';

import { HomeScreen } from '../../../apps/mobile/screens/HomeScreen';

type RouteState =
  | { name: 'Home' }
  | { name: 'Receive'; shareUrl: string };

function normalizeReceiveLink(raw: string | null): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (SHORT_URL_CODE_REGEX.test(trimmed)) {
    return `${SHORT_URL_ORIGIN}/${trimmed}`;
  }

  return trimmed;
}

function getInitialRoute(): RouteState {
  if (typeof window !== 'undefined') {
    const state = window.history.state as RouteState | null;
    if (state?.name === 'Receive' && state.shareUrl) {
      return state;
    }

    const search = window.location.search;
    const params = new URLSearchParams(search);
    const payload = normalizeReceiveLink(
      params.get('share') || params.get('p') || params.get('hash')
    );

    if (payload) {
      return { name: 'Receive', shareUrl: payload };
    }
  }
  return { name: 'Home' };
}

export function AppNavigator() {
  const [route, setRoute] = useState<RouteState>(() => getInitialRoute());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onPopState = () => {
      setRoute(getInitialRoute());
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigateHome = useCallback((replace = false) => {
    if (typeof window !== 'undefined') {
      const method = replace ? 'replaceState' : 'pushState';
      window.history[method]({ name: 'Home' }, '', window.location.pathname);
    }
    setRoute({ name: 'Home' });
  }, []);

  const navigateReceive = useCallback((shareUrl: string, replace = false) => {
    if (typeof window !== 'undefined') {
      const method = replace ? 'replaceState' : 'pushState';
      window.history[method]({ name: 'Receive', shareUrl }, '', window.location.pathname);
    }
    setRoute({ name: 'Receive', shareUrl });
  }, []);

  useEffect(() => {
    if (route.name === 'Receive' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('share') || params.get('p') || params.get('hash')) {
        navigateReceive(route.shareUrl, true);
      }
    }
  }, [route, navigateReceive]);

  return (
    <View style={styles.container}>
      <HomeScreen
        initialJoinCode={route.name === 'Receive' ? route.shareUrl : undefined}
        initialTab={route.name === 'Receive' ? 'receive' : 'send'}
        onNavigateToReceive={(shareUrl) => navigateReceive(shareUrl)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    height: '100%',
    backgroundColor: '#0a0a0f',
  },
});
